import { type FeatureInfoResponse } from "@faro-lotv/service-wires";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

export type SubscriptionFeaturesState = {
  /**
   * Information about all available features for the current project context.
   * The information stored in here applies only to the current project and user.
   *
   * Check the response details to determine whether a feature is not just available, but also permitted and enabled.
   * Available: An admin panel plan grants availability for this feature in the current project.
   * Enabled:   The feature was enabled for the current project in the dashboard.
   *            Only applies to some features.
   * Permitted: The feature is allowed for the current user.
   *            E.g. when opening a project incognito, all "write" features will not be permitted.
   */
  featuresAvailable?: FeatureInfoResponse[];

  /**
   * Information about all available features for the current company context.
   *
   * Some features are only granted at company level and therefore never appear in `featuresAvailable`.
   */
  companyFeaturesAvailable?: FeatureInfoResponse[];
};

const initialState: Readonly<SubscriptionFeaturesState> = Object.freeze({
  featuresAvailable: undefined,
  companyFeaturesAvailable: undefined,
});

const subscriptionFeaturesSlice = createSlice({
  initialState,
  name: "subscriptionFeatures",
  reducers: {
    initFeaturesAvailable(state, action: PayloadAction<FeatureInfoResponse[]>) {
      state.featuresAvailable = action.payload;
    },

    initCompanyFeaturesAvailable(
      state,
      action: PayloadAction<FeatureInfoResponse[]>,
    ) {
      state.companyFeaturesAvailable = action.payload;
    },
  },
});

export const { initFeaturesAvailable, initCompanyFeaturesAvailable } =
  subscriptionFeaturesSlice.actions;

export const subscriptionFeaturesReducer = subscriptionFeaturesSlice.reducer;
